/* eslint-disable react/prop-types */import { formatDateDDMMYYYY, formatDateTimeDDMMYYYY } from '../../utils/dateUtils';
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from 'sweetalert2';
import {
  Grid,
  Card,
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  ManageAccounts as ManageIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
  Science as ScienceIcon,
  Assignment as AssignmentIcon,
  Notes as NotesIcon,
  CloudUpload as CloudUploadIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as FileIcon,
  EventNote as EventNoteIcon,
  DocumentScanner as ScannerIcon,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDDatePicker from "components/MDDatePicker";
import RichTextEditor from "components/RichTextEditor";
import LoaderOverlay from "components/LoaderOverlay";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import FollowUpDialog from "components/FollowUpDialog";
import ScanPrescriptionDialog from "layouts/prescription/ScanPrescriptionDialog";
import { api } from "../../api";

// Constants for consistent styling
const selectBoxHeight = 45;

function PatientPrescriptionSearch() {
  const navigate = useNavigate();
  const { patientCode, coupleCode } = useParams();

  // State management
  const [loading, setLoading] = useState(false);
  const [prescriptions, setPrescriptions] = useState([]);
  const [patientInfo, setPatientInfo] = useState(null);
  const [statusFilter, setStatusFilter] = useState("DRAFT"); // Default to DRAFT
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Synopsis dialog state
  const [synopsisDialogOpen, setSynopsisDialogOpen] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [synopsisText, setSynopsisText] = useState("");
  const [probableDiagonosisFemale, setProbableDiagonosisFemale] = useState("");
  const [probableDiagonosisMale, setProbableDiagonosisMale] = useState("");

  // File upload dialog state
  const [filesDialogOpen, setFilesDialogOpen] = useState(false);
  const [scanFiles, setScanFiles] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [fileDescription, setFileDescription] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  // AI Scan dialog state
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [selectedScanPrescription, setSelectedScanPrescription] = useState(null);

  // Follow-up dialog state
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [followUpPrescription, setFollowUpPrescription] = useState(null);
  const [followUpDoctors, setFollowUpDoctors] = useState([]);
  const [followUpDefaultDoctor, setFollowUpDefaultDoctor] = useState(null);

  const currentUserId = (() => {
    try {
      const raw = localStorage.getItem('user') || localStorage.getItem('userData');
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed?.user_id || parsed?.id || 1;
      }
    } catch { /* ignore */ }
    return 1;
  })();

  // Load patient info and prescriptions data
  useEffect(() => {
    if (patientCode) {
      loadPatientInfo();
    }
  }, [patientCode, coupleCode]);

  // Load prescriptions after patient info is loaded
  useEffect(() => {
    if (patientInfo?.contact?.id) {
      loadPrescriptions();
    }
  }, [patientInfo, statusFilter, startDate, endDate]);

  const loadPatientInfo = async () => {
    try {
      setLoading(true); // Show loader while fetching patient info
      // Get patient info using the contact API with patient code from URL
      const response = await api.contacts.getPatientDetailsByExternalCode(patientCode);
      if (response?.data) {
        setPatientInfo(response.data);
      }
    } catch (error) {
      console.error("Error loading patient info:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to load patient information. Please check if the patient code is valid.',
        showConfirmButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPrescriptions = async () => {
    // Don't load prescriptions if patient info is not available
    if (!patientInfo?.contact?.id) {
      console.log("Patient info not available, skipping prescription search");
      return;
    }

    setLoading(true);
    try {
      const searchParams = {
        contact_id: patientInfo.contact.id, // Always include contact_id for filtering
        status: statusFilter !== "ALL" ? statusFilter : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        order_by: 'created_at',
        order_direction: 'DESC', // Latest first
      };

      // Remove undefined values
      const filteredParams = Object.fromEntries(
        Object.entries(searchParams).filter(([_, v]) => v !== undefined)
      );

      console.log("Searching prescriptions with params:", filteredParams);

      const response = await api.opd.searchPatientPrescriptions(patientCode, filteredParams);
      
      if (response?.data) {
        // Server-side filtering by contact_id should be sufficient now, but keep client-side as backup
        let filteredPrescriptions = response.data;
        if (patientInfo?.contact?.id) {
          filteredPrescriptions = response.data.filter(prescription => 
            prescription.irm_contacts_id === patientInfo.contact.id || 
            prescription.contact_id === patientInfo.contact.id
          );
        }
        setPrescriptions(filteredPrescriptions);

        console.log(`Found ${filteredPrescriptions.length} prescriptions for contact ID: ${patientInfo.contact.id}`);
      } else {
        setPrescriptions([]);
      }
    } catch (error) {
      console.error("Error loading prescriptions:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to load prescriptions. Please try again.',
        showConfirmButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
      setPrescriptions([]);
    } finally {
      setLoading(false);
    }
  };

  // AI Scan: open dialog for a specific prescription row
  const handleOpenScanDialog = (prescription) => {
    setSelectedScanPrescription(prescription);
    setScanDialogOpen(true);
  };

  // AI Scan: save synopsis and diagnosis fields to the prescription
  const handleScanUpdateSynopsis = async ({ synopsis, probable_diagnosis_female, probable_diagnosis_male }) => {
    const prescription = selectedScanPrescription;
    if (!prescription) return;
    await api.opd.updatePrescription(prescription.id, {
      synopsis: synopsis || undefined,
      probable_diagonosis_female: probable_diagnosis_female || undefined,
      probable_diagonosis_male: probable_diagnosis_male || undefined,
      updated_by: currentUserId,
    });
  };

  // AI Scan: create a follow-up from the extracted date
  const handleScanCreateFollowUp = async ({ followupDate }) => {
    const prescription = selectedScanPrescription;
    if (!prescription || !followupDate) return;
    await api.opd.createFollowup({
      irm_contacts_id: prescription.irm_contacts_id || patientInfo?.contact?.id,
      irm_prescriptions_id: prescription.id,
      irm_users_id: prescription.irm_users_id,
      probable_date: followupDate,
      reason: 'Follow-up from AI prescription scan',
      created_by: currentUserId,
    });
  };

  // AI Scan: apply extracted data to the selected prescription then navigate to ManagePrescription
  const handleScanApplyForPrescription = async ({ meds, invs, vitals, jobRef }) => {
    setScanDialogOpen(false);
    const prescription = selectedScanPrescription;
    if (!prescription) return;
    setLoading(true);
    try {
      if (vitals) {
        await api.opd.updatePrescription(prescription.id, {
          weight: vitals.weight || undefined,
          height: vitals.height || undefined,
          bp_systolic: vitals.bp_systolic || undefined,
          bp_diastolic: vitals.bp_diastolic || undefined,
          synopsis: vitals.synopsis || undefined,
          probable_diagonosis_female: vitals.probable_diagonosis_female || undefined,
          probable_diagonosis_male: vitals.probable_diagonosis_male || undefined,
          remarks: vitals.remarks || undefined,
          updated_by: currentUserId,
        });
      }

      const cleanMeds = (meds || []).filter(m => m.name && m.name.trim() !== '');
      const cleanInvs = (invs || []).filter(inv => inv.name && inv.name.trim() !== '');

      if (cleanMeds.length > 0 || cleanInvs.length > 0) {
        await api.opd.managePrescriptionComponents({
          prescription_id: prescription.id,
          prescription_code: prescription.prescription_code,
          contact_id: patientInfo?.contact?.id,
          created_by: currentUserId,
          prescription_medications: cleanMeds,
          prescription_investigations: cleanInvs,
        });
      }

      if (jobRef) {
        api.opd.linkAiScanToPrescription(jobRef, prescription.id).catch(() => {});
      }

      navigate(`/prescription/manage/${patientCode}/${coupleCode || 'undefined'}/${prescription.prescription_code}`);
    } catch (err) {
      console.error('Error applying AI scan to prescription:', err);
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to apply scan data to prescription. Please try again.',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK',
      });
    }
  };

  // Follow-up: open dialog pre-populated from prescription
  const handleOpenFollowUp = async (prescription) => {
    setFollowUpPrescription(prescription);

    // Default doctor = this prescription's doctor
    const prescriptionDoctorId = prescription.irm_users_id || null;
    let doctorList = prescriptionDoctorId
      ? [{ id: prescriptionDoctorId, name: `Doctor #${prescriptionDoctorId}` }]
      : [];

    // Try to load a proper doctor name and full user list
    try {
      const usersResp = api.user.getActiveUsers ? await api.user.getActiveUsers() : null;
      if (usersResp?.data?.data?.length) {
        doctorList = usersResp.data.data.map(u => ({
          id: u.id,
          name: u.name || u.full_name || `User #${u.id}`,
        }));
      }
    } catch { /* fall back to prescription doctor only */ }

    setFollowUpDoctors(doctorList);
    setFollowUpDefaultDoctor(prescriptionDoctorId);
    setFollowUpDialogOpen(true);
  };

  // Handle search
  const handleSearch = () => {
    loadPrescriptions();
  };

  // Clear all filters
  const handleClearFilters = () => {
    setStatusFilter("DRAFT"); // Reset to DRAFT instead of ALL
    setStartDate("");
    setEndDate("");
    // Reload with cleared filters
    setTimeout(() => {
      loadPrescriptions();
    }, 100);
  };

  // Status color mapping
  const getStatusColor = (status) => {
    switch (status) {
      case "DRAFT":
        return "default";
      case "PUBLISHED":
        return "success";
      case "RECALLED":
        return "error";
      case "REPUBLISHED":
        return "warning";
      default:
        return "default";
    }
  };

  // Action handlers
  const handleViewPrescription = (prescription) => {
    navigate(`/prescription/view/${patientCode}/${coupleCode || 'undefined'}/${prescription.prescription_code}`);
  };

  const handleEditPrescription = (prescription) => {
    // Navigate to manage prescription page with patient code, couple code, and prescription ID
    navigate(`/prescription/manage/${patientCode}/${coupleCode || 'undefined'}/${prescription.prescription_code}`);
  };

  const handlePublishPrescription = async (prescription) => {
    const result = await Swal.fire({
      title: 'Publish Prescription?',
      text: `Are you sure you want to publish prescription ${prescription.prescription_code}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00798E',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, publish it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        // Use the prescription ID and pass userId (you should get this from auth context)
        await api.opd.publishPrescription(prescription.id, 1);
        
        // Hide loader before showing success message
        setLoading(false);
        
        await Swal.fire({
          icon: 'success',
          title: 'Published!',
          text: 'Prescription has been published successfully.',
          showConfirmButton: true,
          confirmButtonColor: '#00798E',
          confirmButtonText: 'OK'
        });

        // Reload the prescriptions list
        loadPrescriptions();
      } catch (error) {
        setLoading(false);
        console.error("Error publishing prescription:", error);
        await Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Failed to publish prescription. Please try again.',
          showConfirmButton: true,
          confirmButtonColor: '#d33',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  const handleDeletePrescription = async (prescription) => {
    const result = await Swal.fire({
      title: 'Delete Prescription?',
      text: `Are you sure you want to delete prescription ${prescription.prescription_code}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#00798E',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await api.opd.deletePrescription(prescription.id);
        
        await Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Prescription has been deleted successfully.',
          showConfirmButton: true,
          confirmButtonColor: '#00798E',
          confirmButtonText: 'OK'
        });

        // Reload the prescriptions list
        loadPrescriptions();
      } catch (error) {
        console.error("Error deleting prescription:", error);
        await Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Failed to delete prescription. Please try again.',
          showConfirmButton: true,
          confirmButtonColor: '#d33',
          confirmButtonText: 'OK'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Synopsis update handlers
  const handleOpenSynopsisDialog = (prescription) => {
    setSelectedPrescription(prescription);
    setSynopsisText(prescription.synopsis || "");
    setProbableDiagonosisFemale(prescription.probable_diagonosis_female || "");
    setProbableDiagonosisMale(prescription.probable_diagonosis_male || "");
    setSynopsisDialogOpen(true);
  };

  const handleCloseSynopsisDialog = () => {
    setSynopsisDialogOpen(false);
    setSelectedPrescription(null);
    setSynopsisText("");
    setProbableDiagonosisFemale("");
    setProbableDiagonosisMale("");
  };

  const handleSaveSynopsis = async () => {
    if (!selectedPrescription) return;

    try {
      setLoading(true);
      await api.opd.updatePrescription(selectedPrescription.id, {
        synopsis: synopsisText,
        probable_diagonosis_female: probableDiagonosisFemale,
        probable_diagonosis_male: probableDiagonosisMale,
        updated_by: 1, // TODO: Get from auth context
      });

      setLoading(false);
      
      await Swal.fire({
        icon: 'success',
        title: 'Updated!',
        text: 'Synopsis has been updated successfully.',
        showConfirmButton: true,
        confirmButtonColor: '#00798E',
        confirmButtonText: 'OK'
      });

      handleCloseSynopsisDialog();
      loadPrescriptions();
    } catch (error) {
      setLoading(false);
      console.error("Error updating synopsis:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to update synopsis. Please try again.',
        showConfirmButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    }
  };

  // File upload handlers
  const handleOpenFilesDialog = async (prescription) => {
    setSelectedPrescription(prescription);
    setFilesDialogOpen(true);
    await loadScanFiles(prescription.id);
  };

  const handleCloseFilesDialog = () => {
    setFilesDialogOpen(false);
    setSelectedPrescription(null);
    setScanFiles([]);
    setUploadFile(null);
    setFileDescription("");
    setIsDragging(false);
  };

  const loadScanFiles = async (prescriptionId) => {
    try {
      setLoading(true);
      const response = await api.opd.getPrescriptionScanFiles(prescriptionId);
      console.log("Scan files response:", response);
      if (response?.data) {
        setScanFiles(response.data);
        console.log("Scan files loaded:", response.data);
      } else {
        setScanFiles([]);
      }
    } catch (error) {
      console.error("Error loading scan files:", error);
      setScanFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid File Type',
          text: 'Only JPG, PNG, PDF, GIF, and WEBP files are allowed.',
          confirmButtonColor: '#d33',
        });
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire({
          icon: 'error',
          title: 'File Too Large',
          text: 'File size must be less than 10MB.',
          confirmButtonColor: '#d33',
        });
        return;
      }

      setUploadFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect({ target: { files: [file] } });
    }
  };

  const handleUploadFile = async () => {
    if (!uploadFile || !selectedPrescription) return;

    try {
      setLoading(true);
      await api.opd.uploadPrescriptionScanFile(
        selectedPrescription.id,
        uploadFile,
        fileDescription
      );

      // Reset upload form
      setUploadFile(null);
      setFileDescription("");
      
      // Reload files
      const response = await api.opd.getPrescriptionScanFiles(selectedPrescription.id);
      if (response?.data) {
        setScanFiles(response.data);
      }
      
      setLoading(false);

      // Show success message with higher z-index
      await Swal.fire({
        icon: 'success',
        title: 'Uploaded!',
        text: 'File has been uploaded successfully.',
        confirmButtonColor: '#00798E',
        customClass: {
          container: 'swal-high-zindex'
        },
        didOpen: () => {
          // Ensure SweetAlert2 appears above MUI Dialog (default z-index is 1300)
          const swalContainer = document.querySelector('.swal2-container');
          if (swalContainer) {
            swalContainer.style.zIndex = '9999';
          }
        }
      });

    } catch (error) {
      setLoading(false);
      console.error("Error uploading file:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to upload file. Please try again.',
        confirmButtonColor: '#d33',
        customClass: {
          container: 'swal-high-zindex'
        },
        didOpen: () => {
          // Ensure SweetAlert2 appears above MUI Dialog
          const swalContainer = document.querySelector('.swal2-container');
          if (swalContainer) {
            swalContainer.style.zIndex = '9999';
          }
        }
      });
    }
  };

  const handleViewFile = (file) => {
    const fileUrl = `${file.base_url}${file.file_path}`;
    window.open(fileUrl, '_blank');
  };

  const handleDeleteFile = async (file) => {
    const result = await Swal.fire({
      title: 'Delete File?',
      text: `Are you sure you want to delete ${file.file_name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#00798E',
      confirmButtonText: 'Yes, delete it!',
      didOpen: () => {
        // Ensure SweetAlert2 appears above MUI Dialog
        const swalContainer = document.querySelector('.swal2-container');
        if (swalContainer) {
          swalContainer.style.zIndex = '9999';
        }
      }
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await api.opd.deletePrescriptionScanFile(file.id, 1); // TODO: Get user ID from auth
        
        await Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'File has been deleted.',
          confirmButtonColor: '#00798E',
          didOpen: () => {
            const swalContainer = document.querySelector('.swal2-container');
            if (swalContainer) {
              swalContainer.style.zIndex = '9999';
            }
          }
        });

        await loadScanFiles(selectedPrescription.id);
      } catch (error) {
        console.error("Error deleting file:", error);
        await Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Failed to delete file.',
          confirmButtonColor: '#d33',
          didOpen: () => {
            const swalContainer = document.querySelector('.swal2-container');
            if (swalContainer) {
              swalContainer.style.zIndex = '9999';
            }
          }
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Table columns configuration
  const columns = [
    {
      Header: "Prescription Code",
      accessor: "prescription_code",
      Cell: ({ value }) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {value}
        </Typography>
      ),
    },
    {
      Header: "Type",
      accessor: "type",
      Cell: ({ value }) => (
        <Chip
          label={value}
          color="primary"
          size="small"
          variant="filled"
          sx={{ minWidth: 60 }}
        />
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <Chip
          label={value}
          color={getStatusColor(value)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      Header: "Date Created",
      accessor: "created_at",
      Cell: ({ value }) => (
        <Typography variant="body2">
          {formatDateTimeDDMMYYYY(value)}
        </Typography>
      ),
    },
    {
      Header: "Last Updated",
      accessor: "updated_at",
      Cell: ({ value }) => (
        <Typography variant="body2">
          {formatDateDDMMYYYY(value)}
        </Typography>
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => {
        const prescription = row.original;
        const isDraft = prescription.status === "DRAFT";
        const isPublished = prescription.status === "PUBLISHED";
        
        return (
          <Box display="flex" justifyContent="center" gap={0.5}>
            {/* View Action - Always available */}
            <Tooltip title="View Prescription">
              <IconButton
                size="small"
                onClick={() => handleViewPrescription(prescription)}
                sx={{ 
                  color: "#00798E",
                  "&:hover": { backgroundColor: "rgba(0, 121, 142, 0.04)" },
                }}
              >
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Update Synopsis Action - Available for DRAFT */}
            {isDraft && (
              <Tooltip title="Update Synopsis">
                <IconButton
                  size="small"
                  onClick={() => handleOpenSynopsisDialog(prescription)}
                  sx={{
                    color: "#9c27b0",
                    "&:hover": { backgroundColor: "rgba(156, 39, 176, 0.04)" },
                  }}
                >
                  <NotesIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* Create Follow-Up Action - Available for all statuses */}
            <Tooltip title="Create Follow-Up">
              <IconButton
                size="small"
                onClick={() => handleOpenFollowUp(prescription)}
                sx={{
                  color: "#00798E",
                  "&:hover": { backgroundColor: "rgba(0, 121, 142, 0.08)" },
                }}
              >
                <EventNoteIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Files Action - Available for DRAFT */}
            {isDraft && (
              <Tooltip title="Manage Files">
                <IconButton
                  size="small"
                  onClick={() => handleOpenFilesDialog(prescription)}
                  sx={{
                    color: "#f57c00",
                    "&:hover": { backgroundColor: "rgba(245, 124, 0, 0.04)" },
                  }}
                >
                  <AttachFileIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* AI Scan Action - Available for DRAFT */}
            {isDraft && (
              <Tooltip title="Scan Prescription (AI)">
                <IconButton
                  size="small"
                  onClick={() => handleOpenScanDialog(prescription)}
                  sx={{
                    color: "#7b1fa2",
                    "&:hover": { backgroundColor: "rgba(123, 31, 162, 0.06)" },
                  }}
                >
                  <ScannerIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* Edit/Manage Action - Available for DRAFT - DISABLED */}
            {isDraft && (
              <Tooltip title="Manage Prescription (Disabled)">
                <span>
                  <IconButton
                    size="small"
                    disabled
                    sx={{ 
                      color: "#ff9800",
                      opacity: 0.3,
                    }}
                  >
                    <ManageIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {/* Publish Action - Available for DRAFT */}
            {isDraft && (
              <Tooltip title="Publish Prescription">
                <IconButton
                  size="small"
                  onClick={() => handlePublishPrescription(prescription)}
                  sx={{ 
                    color: "#4caf50",
                    "&:hover": { backgroundColor: "rgba(76, 175, 80, 0.04)" },
                  }}
                >
                  <PublishIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* Edit Action - Available for PUBLISHED/RECALLED/REPUBLISHED */}
            {!isDraft && (
              <Tooltip title="Edit Prescription">
                <IconButton
                  size="small"
                  onClick={() => handleEditPrescription(prescription)}
                  sx={{ 
                    color: "#2196f3",
                    "&:hover": { backgroundColor: "rgba(33, 150, 243, 0.04)" },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* Delete Action - Always available */}
            {/* Temporarily disabled delete functionality
            <Tooltip title="Delete Prescription">
              <IconButton
                size="small"
                onClick={() => handleDeletePrescription(prescription)}
                sx={{ 
                  color: "#f44336",
                  "&:hover": { backgroundColor: "rgba(244, 67, 54, 0.04)" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            */}
          </Box>
        );
      },
    },
  ];

  // Table data with actions
  const tableData = prescriptions.map((prescription) => ({
    ...prescription,
    actions: prescription.id, // This will be handled by the Cell component
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loading} />
      
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                bgColor="logo"
                borderRadius="lg"
                coloredShadow="logo"
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center">
                    <IconButton
                      onClick={() => navigate(-1)}
                      sx={{ color: 'white', mr: 1 }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <MDTypography variant="h6" color="white">
                      Patient Prescriptions
                    </MDTypography>
                  </Box>
                  
                  {/* Shortcut Navigation Buttons */}
                  <Box display="flex" gap={1}>
                    <Tooltip title="Manage Contact Histories" placement="top" 
                      sx={{ 
                        '& .MuiTooltip-tooltip': { 
                          backgroundColor: '#333', 
                          color: 'white',
                          fontSize: '0.75rem'
                        } 
                      }}>
                      <IconButton
                        onClick={() => navigate(`/contact-histories/${patientCode}/${coupleCode || patientCode}`)}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: 'white',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '50%',
                          width: '40px',
                          height: '40px',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                          },
                          transition: 'all 0.2s ease-in-out',
                          '& .MuiSvgIcon-root': {
                            color: 'white'
                          }
                        }}
                      >
                        <HistoryIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Manage Investigations" placement="top"
                      sx={{ 
                        '& .MuiTooltip-tooltip': { 
                          backgroundColor: '#333', 
                          color: 'white',
                          fontSize: '0.75rem'
                        } 
                      }}>
                      <IconButton
                        onClick={() => navigate(`/patient-investigations/${patientCode}/${coupleCode || patientCode}`)}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: 'white',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '50%',
                          width: '40px',
                          height: '40px',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                          },
                          transition: 'all 0.2s ease-in-out',
                          '& .MuiSvgIcon-root': {
                            color: 'white'
                          }
                        }}
                      >
                        <ScienceIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Infertility Summary" placement="top"
                      sx={{ 
                        '& .MuiTooltip-tooltip': { 
                          backgroundColor: '#333', 
                          color: 'white',
                          fontSize: '0.75rem'
                        } 
                      }}>
                      <IconButton
                        onClick={() => navigate(`/infertility-summary/${patientCode}/${coupleCode || patientCode}`)}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: 'white',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '50%',
                          width: '40px',
                          height: '40px',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                          },
                          transition: 'all 0.2s ease-in-out',
                          '& .MuiSvgIcon-root': {
                            color: 'white'
                          }
                        }}
                      >
                        <AssignmentIcon />
                      </IconButton>
                    </Tooltip>

                  </Box>
                </Box>
              </MDBox>

              {/* Patient Info Summary */}
              {patientInfo && (
                <MDBox p={2} sx={{ backgroundColor: 'rgba(0, 121, 142, 0.05)' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        Patient Name
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {patientInfo?.contact?.full_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        Patient Code
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {patientInfo?.contact?.external_code}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        Gender
                      </Typography>
                      <Typography variant="body1">
                        {patientInfo?.contact?.gender}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        Phone Number
                      </Typography>
                      <Typography variant="body1">
                        {patientInfo?.phones?.[0]?.phone_number || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </MDBox>
              )}

              {/* Search and Filter Section */}
              <MDBox p={3}>
                <Grid container spacing={3} alignItems="center">
                  {/* Status Filter */}
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel 
                        sx={{ 
                          color: '#00798E',
                          '&.Mui-focused': { color: '#00798E' }
                        }}
                      >
                        Status
                      </InputLabel>
                      <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{
                          height: selectBoxHeight,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00798E',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00798E',
                          },
                        }}
                      >
                        <MenuItem value="ALL">All Status</MenuItem>
                        <MenuItem value="DRAFT">Draft</MenuItem>
                        <MenuItem value="PUBLISHED">Published</MenuItem>
                        <MenuItem value="RECALLED">Recalled</MenuItem>
                        <MenuItem value="REPUBLISHED">Republished</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Date Range */}
                  <Grid item xs={6} md={3}>
                    <MDDatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(date) => setStartDate(date)}
                      textFieldProps={{
                        fullWidth: true,
                        margin: "normal",
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            height: selectBoxHeight,
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                            },
                            '&:hover fieldset': {
                              borderColor: '#00798E',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#00798E',
                            },
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <MDDatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(date) => setEndDate(date)}
                      textFieldProps={{
                        fullWidth: true,
                        margin: "normal",
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            height: selectBoxHeight,
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                            },
                            '&:hover fieldset': {
                              borderColor: '#00798E',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#00798E',
                            },
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Action Buttons */}
                  <Grid item xs={12} md={3}>
                    <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
                      <MDButton
                        variant="gradient"
                        color="logo"
                        startIcon={<SearchIcon />}
                        onClick={handleSearch}
                        sx={{
                          minWidth: '100px',
                          height: selectBoxHeight,
                        }}
                      >
                        Search
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="logo"
                        startIcon={<ClearIcon />}
                        onClick={handleClearFilters}
                        sx={{
                          minWidth: '90px',
                          height: selectBoxHeight,
                          borderColor: "#00798E",
                          color: "#00798E",
                          "&:hover": { 
                            borderColor: "#005f6b",
                            backgroundColor: "rgba(0, 121, 142, 0.1)"
                          },
                        }}
                      >
                        Clear
                      </MDButton>
                    </Box>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Results Table */}
              <MDBox pb={3} px={3}>
                {prescriptions.length > 0 ? (
                  <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="body2" color="textSecondary">
                        {prescriptions.length} prescription(s) found (sorted by latest first)
                      </Typography>
                      <MDButton
                        variant="gradient"
                        color="logo"
                        startIcon={<AddIcon />}
                        onClick={() => navigate(`/prescription/create/draft/${patientCode}/${coupleCode || 'undefined'}`)}
                        sx={{
                          minWidth: '180px',
                          height: '36px',
                          fontSize: '0.875rem',
                        }}
                      >
                        Create New Prescription
                      </MDButton>
                    </Box>
                    <Card>
                      <MDBox pt={3}>
                        <DataTable
                          table={{ columns, rows: tableData }}
                          isSorted={false}
                          entriesPerPage={true}
                          showTotalEntries={true}
                          noEndBorder
                        />
                      </MDBox>
                    </Card>
                  </>
                ) : (
                  <Box textAlign="center" py={6}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      No prescriptions found
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                      This patient doesn&apos;t have any prescriptions matching your criteria.
                    </Typography>
                    <MDButton
                      variant="gradient"
                      color="logo"
                      startIcon={<AddIcon />}
                      onClick={() => navigate(`/prescription/create/draft/${patientCode}/${coupleCode || 'undefined'}`)}
                      sx={{
                        minWidth: '200px',
                        height: '45px',
                      }}
                    >
                      Create First Prescription
                    </MDButton>
                  </Box>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      
      {/* Synopsis Update Dialog */}
      <Dialog 
        open={synopsisDialogOpen} 
        onClose={handleCloseSynopsisDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Update Synopsis
          {selectedPrescription && (
            <Typography variant="caption" display="block" color="text.secondary">
              Prescription: {selectedPrescription.prescription_code}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Probable Diagnosis (Female)
              </Typography>
              <RichTextEditor
                value={probableDiagonosisFemale}
                onChange={(value) => setProbableDiagonosisFemale(value)}
                height="140px"
                placeholder="Enter probable diagnosis (female)..."
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Probable Diagnosis (Male)
              </Typography>
              <RichTextEditor
                value={probableDiagonosisMale}
                onChange={(value) => setProbableDiagonosisMale(value)}
                height="140px"
                placeholder="Enter probable diagnosis (male)..."
              />
            </Box>
          </Box>
          <Box sx={{ mt: 2 }}>
            <RichTextEditor
              value={synopsisText}
              onChange={(value) => setSynopsisText(value)}
              height="250px"
              placeholder="Enter prescription synopsis here..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <button
            onClick={handleCloseSynopsisDialog}
            style={{
              padding: '10px 24px',
              background: '#6c757d',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveSynopsis}
            style={{
              padding: '10px 24px',
              background: '#00798E',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 4px rgba(0, 121, 142, 0.2)',
            }}
          >
            Save Synopsis
          </button>
        </DialogActions>
      </Dialog>

      {/* Files Management Dialog */}
      <Dialog 
        open={filesDialogOpen} 
        onClose={handleCloseFilesDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Manage Prescription Files
          {selectedPrescription && (
            <Typography variant="caption" display="block" color="text.secondary">
              Prescription: {selectedPrescription.prescription_code}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {/* Existing Files List */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Uploaded Files ({scanFiles.length})
            </Typography>
            {scanFiles.length > 0 ? (
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {scanFiles.map((file) => (
                  <Box
                    key={file.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      mb: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <FileIcon sx={{ mr: 2, color: '#00798E' }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {file.file_name}
                        </Typography>
                        {file.description && (
                          <Typography variant="caption" color="text.secondary">
                            {file.description}
                          </Typography>
                        )}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {(file.file_size / 1024).toFixed(2)} KB • {formatDateTimeDDMMYYYY(file.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewFile(file)}
                        sx={{
                          color: '#00798E',
                          '&:hover': { backgroundColor: 'rgba(0, 121, 142, 0.04)' },
                        }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteFile(file)}
                        sx={{
                          color: '#f44336',
                          '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.04)' },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No files uploaded yet
              </Typography>
            )}
          </Box>

          <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Upload New File
            </Typography>

            {/* Drag and Drop Area */}
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: isDragging ? '2px dashed #00798E' : '2px dashed #ccc',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                backgroundColor: isDragging ? 'rgba(0, 121, 142, 0.05)' : '#fafafa',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                mb: 2,
              }}
              onClick={() => document.getElementById('file-upload-input').click()}
            >
              <input
                id="file-upload-input"
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,.gif,.webp"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <CloudUploadIcon sx={{ fontSize: 48, color: '#00798E', mb: 2 }} />
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                {uploadFile ? uploadFile.name : 'Drag and drop file here'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                or click to browse (Max 10MB - JPG, PNG, PDF, GIF, WEBP)
              </Typography>
            </Box>

            {/* File Description */}
            {uploadFile && (
              <TextField
                fullWidth
                label="FILE DESCRIPTION (OPTIONAL)"
                value={fileDescription}
                onChange={(e) => setFileDescription(e.target.value.toUpperCase())}
                variant="outlined"
                size="small"
                sx={{ mb: 2 }}
                inputProps={{ 
                  maxLength: 256,
                  style: { textTransform: 'uppercase' }
                }}
                helperText={`${fileDescription.length}/256 characters`}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <button
            onClick={handleCloseFilesDialog}
            style={{
              padding: '10px 24px',
              background: '#6c757d',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            Close
          </button>
          {uploadFile && (
            <button
              onClick={handleUploadFile}
              style={{
                padding: '10px 24px',
                background: '#00798E',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 4px rgba(0, 121, 142, 0.2)',
              }}
            >
              Upload File
            </button>
          )}
        </DialogActions>
      </Dialog>

      <Footer />

      {/* Follow-Up Dialog — pre-populated from selected prescription */}
      <FollowUpDialog
        open={followUpDialogOpen}
        onClose={() => {
          setFollowUpDialogOpen(false);
          setFollowUpPrescription(null);
        }}
        onSuccess={() => {
          setFollowUpDialogOpen(false);
          setFollowUpPrescription(null);
          Swal.fire({
            icon: 'success',
            title: 'Follow-Up Created',
            text: 'The follow-up has been scheduled successfully.',
            confirmButtonColor: '#00798E',
            confirmButtonText: 'OK',
          });
        }}
        contactId={patientInfo?.contact?.id}
        doctors={followUpDoctors}
        defaultDoctorId={followUpDefaultDoctor}
        prescriptionId={followUpPrescription?.id || null}
        treatmentLine={
          followUpPrescription
            ? [
                followUpPrescription.probable_diagonosis_female,
                followUpPrescription.probable_diagonosis_male,
              ]
                .filter(Boolean)
                .map(t => t.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim())
                .join(' | ') || null
            : null
        }
        currentUserId={currentUserId}
      />

      {/* AI Scan Dialog — scans & applies to the selected prescription row */}
      <ScanPrescriptionDialog
        open={scanDialogOpen}
        onClose={() => { setScanDialogOpen(false); setSelectedScanPrescription(null); }}
        prescriptionId={selectedScanPrescription?.id ?? null}
        prescriptionCode={selectedScanPrescription?.prescription_code}
        onApplyAll={handleScanApplyForPrescription}
        onUpdateSynopsis={handleScanUpdateSynopsis}
        onCreateFollowUp={handleScanCreateFollowUp}
      />
    </DashboardLayout>
  );
}

export default PatientPrescriptionSearch;
